import { Flex, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ChangePasswordForm } from '@/features/auth/change-password';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/shared/config/routes';

/** Root-level guard for every authenticated zone. Bootstrap resolves `auth`'s
 *  status to `authenticated`/`unauthenticated` before the router ever renders a route (see
 *  App.tsx), so `idle` here only covers the brief window before that finishes. A user flagged
 *  `mustChangePassword` (backend flag, e.g. after an admin-forced reset) is blocked
 *  behind a forced change-password screen instead of a dedicated route — changing it logs every
 *  session out anyway, so there's nowhere useful to navigate to until it's done. */
export function RequireAuth() {
  const { t } = useTranslation('auth');
  const status = useAppSelector((state) => state.auth.status);
  const mustChangePassword = useAppSelector(
    (state) => state.auth.user?.mustChangePassword ?? false,
  );
  const location = useLocation();

  if (status === 'idle') {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  if (mustChangePassword) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh', padding: 16 }}>
        <Flex vertical gap={16} style={{ width: '100%', maxWidth: 400 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('changePassword.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('changePassword.mustChange')}</Typography.Text>
          <ChangePasswordForm />
        </Flex>
      </Flex>
    );
  }

  return <Outlet />;
}
